import logger from 'ws-scripts/modules/logger';
import { hysteriFetch, wrappers } from 'hysterics';
import { isBrowser } from 'ws-scripts/modules/environment';
import { addNewRelicPageAction } from 'ddc-new-relic';
import { publishEvent } from './pubsub';
import { getEnv } from './getEnv';
import { getDDCLocationsData } from './ddcHelper';
import { DEFAULT_LOCATION_COUNT } from '../global-constants';

const LOG_PREFIX = '[ws-locations-data][locationFetch]';

// API Configuration
const API_ENDPOINT = '/api/widget/ws-locations-data/locations';

const timeouts = {
	localhost: 7000,
	nonprod: 5000,
	prod: 3000
};

const env = getEnv();

// Event Topics
const TOPIC_LOCATIONS_REQUEST = 'ws-locations-data/locations-request';
const TOPIC_LOCATIONS = 'ws-locations-data/locations';

// Wrapped fetch with circuit breaker and retry logic
const requestLocationsWrapper = hysteriFetch(
	'ws-locations-data-request-locations',
	{
		[wrappers.circuitBreaker.contextKey]: {
			timeout: timeouts[env] || 4000
		},
		[wrappers.timeout.contextKey]: {
			timeout: timeouts[env] || 4000
		},
		[wrappers.retry.contextKey]: {
			retries: 0
		}
	}
);

// Build location request URL with query parameters
export const buildLocationRequestUrl = (params) => {
	const urlObj = new URL(API_ENDPOINT, window.location.origin);

	// Dynamically add all non-null/undefined parameters
	Object.entries(params).forEach(([key, value]) => {
		if (value !== undefined && value !== null) {
			urlObj.searchParams.set(key, String(value));
		}
	});

	return urlObj.toString();
};

// Request locations from API with retry logic and error handling
export const requestLocations = (params) => {
	(async () => {
		const startTime = Date.now();
		const ddcLocationsData = getDDCLocationsData();
		const { isDefaultView, ...requestParams } = params;
		const { postalCode, range } = requestParams;
		try {
			publishEvent(TOPIC_LOCATIONS_REQUEST);

			logger.debug(`${LOG_PREFIX} Fetching locations:`, {
				params
			});

			const url = buildLocationRequestUrl(requestParams);
			logger.debug(`${LOG_PREFIX} API URL:`, url);

			const response = await requestLocationsWrapper(url);

			const duration = Date.now() - startTime;

			if (
				!response ||
				!response.locations ||
				!Array.isArray(response.locations)
			) {
				logger.error(`${LOG_PREFIX} Invalid response structure`);
				addNewRelicPageAction('WS LOCATIONS DATA INVALID RESPONSE', {
					locationsPostalCode: postalCode,
					locationsRange: range
				});
				throw new Error('Invalid response structure from server');
			}

			const locations = isDefaultView
				? response.locations.slice(0, DEFAULT_LOCATION_COUNT)
				: response.locations;

			logger.debug(`${LOG_PREFIX} Locations fetched successfully:`, {
				count: locations.length,
				postalCode,
				range,
				duration: `${duration}ms`
			});

			// Track fetch performance and business metrics in New Relic
			addNewRelicPageAction('WS LOCATIONS DATA FETCH DURATION', {
				locationsPostalCode: postalCode,
				locationsRange: range,
				locationsCount: response.locations.length,
				locationsFetchDuration: duration
			});

			// Track empty results for coverage analysis
			if (response.locations.length === 0) {
				addNewRelicPageAction('WS LOCATIONS DATA NO RESULTS', {
					locationsPostalCode: postalCode,
					locationsRange: range
				});
			}

			if (isBrowser && ddcLocationsData) {
				ddcLocationsData.locations = locations || [];
				ddcLocationsData.error = null;

				logger.debug(`${LOG_PREFIX} Publishing location update`, {
					locationCount: locations.length,
					postalCode
				});

				publishEvent(TOPIC_LOCATIONS);
			}
		} catch (error) {
			const duration = Date.now() - startTime;

			logger.error(`${LOG_PREFIX} Fetch failed:`, {
				error: error.message,
				postalCode,
				range,
				duration: `${duration}ms`
			});

			// Track API request failure in New Relic
			addNewRelicPageAction('WS LOCATIONS DATA REQUEST FAILED', {
				locationsPostalCode: postalCode,
				locationsRange: range,
				errorMsg: error.message,
				locationsFetchDuration: duration
			});

			if (isBrowser && window.DDC?.LocationsData) {
				ddcLocationsData.locations = [];
				ddcLocationsData.error = error.message;

				logger.debug(`${LOG_PREFIX} Publishing error notification`);
				publishEvent(TOPIC_LOCATIONS);
			}
		}
	})();
};
